import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, MessageCircle, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { BRAND, INDIAN_STATES, resolveWhatsAppNumber } from "@/config";
import { useCustomer } from "@/context/CustomerContext";

const inputCls =
  "w-full rounded-xl border border-white/10 bg-[#0A0B0E]/70 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 transition-colors focus:border-cyan-400/60 focus:outline-none";

function introMessage(c) {
  return [
    "NEW CUSTOMER — VARSHA SUBLIMATION",
    "--------------------------",
    `Name: ${c.full_name}`,
    c.company_name ? `Company: ${c.company_name}` : null,
    `Mobile: ${c.phone}`,
    `City: ${c.city || "—"}`,
    `State: ${c.state}`,
    "",
    "Please share your best rates and catalogue.",
  ]
    .filter(Boolean)
    .join("\n");
}

export default function CustomerGate() {
  const { setCustomer } = useCustomer();
  const [mode, setMode] = useState("register");
  const [routes, setRoutes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [phone, setPhone] = useState("");
  const [form, setForm] = useState({ full_name: "", company_name: "", phone: "", state: "", city: "" });

  useEffect(() => {
    api.get("/whatsapp-routes").then((r) => setRoutes(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const finishRegister = async () => {
    try {
      const { data } = await api.post("/customers/register", { ...form });
      const number = resolveWhatsAppNumber(routes, data.state);
      window.open(`https://wa.me/${number}?text=${encodeURIComponent(introMessage(data))}`, "_blank");
      setCustomer(data);
      toast.success(`Welcome, ${data.full_name.split(" ")[0]} — say hi on WhatsApp to get your rates`);
      if (data.welcome_code) localStorage.setItem("vs_welcome_code", data.welcome_code);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const register = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim()) return toast.error("Please enter your company name");
    setBusy(true);
    await finishRegister();
    setBusy(false);
  };

  const finishLogin = async (number) => {
    try {
      const { data } = await api.post("/customers/login", { phone: number });
      setCustomer(data);
      toast.success(`Welcome back, ${data.full_name.split(" ")[0]}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    await finishLogin(phone);
    setBusy(false);
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#0A0B0E] px-5 py-14" data-testid="customer-gate">
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(160deg,#0E1016_0%,#0A0B0E_55%,#101118_100%)]" />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-lg"
      >
        <div className="mb-8">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-300">
            <span className="h-1.5 w-1.5 rounded-full bg-fuchsia-400" /> Trade access
          </div>
          <h1 className="mt-4 font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl">
            {BRAND}
          </h1>
          <p className="mt-3 text-base text-slate-400">
            Factory-direct sublimation blanks. Tell us who you are and we'll connect you to your regional sales desk with wholesale pricing.
          </p>
        </div>

        <div className="rounded-3xl border border-white/10 bg-[#12141A]/90 p-7 backdrop-blur-xl">
          <div className="mb-6 flex gap-2 rounded-full border border-white/10 p-1">
            <button
              data-testid="gate-mode-register"
              onClick={() => setMode("register")}
              className={`flex-1 rounded-full py-2 text-xs font-mono uppercase tracking-[0.15em] transition-colors ${mode === "register" ? "bg-gradient-to-r from-cyan-400 to-fuchsia-500 text-[#0A0B0E]" : "text-slate-400 hover:text-slate-200"}`}
            >
              New customer
            </button>
            <button
              data-testid="gate-mode-login"
              onClick={() => setMode("login")}
              className={`flex-1 rounded-full py-2 text-xs font-mono uppercase tracking-[0.15em] transition-colors ${mode === "login" ? "bg-gradient-to-r from-cyan-400 to-fuchsia-500 text-[#0A0B0E]" : "text-slate-400 hover:text-slate-200"}`}
            >
              Already registered
            </button>
          </div>

          {mode === "register" ? (
            <form onSubmit={register} className="space-y-3" data-testid="gate-register-form">
              <input data-testid="gate-name-input" required value={form.full_name} onChange={set("full_name")} placeholder="Full name" className={inputCls} />
              <input data-testid="gate-company-input" required value={form.company_name} onChange={set("company_name")} placeholder="Company name" className={inputCls} />
              <input data-testid="gate-phone-input" required inputMode="numeric" value={form.phone} onChange={set("phone")} placeholder="10-digit mobile number" className={inputCls} />
              <div className="grid gap-3 sm:grid-cols-2">
                <select data-testid="gate-state-select" required value={form.state} onChange={set("state")} className={inputCls}>
                  <option value="">Select state</option>
                  {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <input data-testid="gate-city-input" required value={form.city} onChange={set("city")} placeholder="City" className={inputCls} />
              </div>
              <button
                data-testid="gate-register-submit"
                type="submit"
                disabled={busy}
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-fuchsia-500 py-3.5 text-sm font-bold text-[#0A0B0E] transition-transform duration-300 hover:scale-[1.01] active:scale-95 disabled:opacity-50"
              >
                {busy ? "Opening catalogue..." : <><MessageCircle size={16} /> Enter Catalogue & Say Hi</>}
              </button>
              <p className="pt-1 text-center font-mono text-[10px] leading-relaxed text-slate-500">
                Your details open in WhatsApp addressed to your state's sales desk — one tap to send.
              </p>
            </form>
          ) : (
            <form onSubmit={login} className="space-y-3" data-testid="gate-login-form">
              <div className="relative">
                <Phone size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-600" />
                <input
                  data-testid="gate-login-phone-input"
                  required
                  inputMode="numeric"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Registered mobile number"
                  className={`${inputCls} pl-11`}
                />
              </div>
              <button
                data-testid="gate-login-submit"
                type="submit"
                disabled={busy}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-fuchsia-500 py-3.5 text-sm font-bold text-[#0A0B0E] transition-transform duration-300 hover:scale-[1.01] active:scale-95 disabled:opacity-50"
              >
                {busy ? "Checking..." : <>Continue <ArrowRight size={16} /></>}
              </button>
              <p className="pt-1 text-center font-mono text-[10px] text-slate-500">
                No password, no OTP — just your number.
              </p>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
}
